import { HttpClient } from "../http";
import {
  CheckoutSessionPayload,
  CheckoutSessionResponse,
  RequestOptions,
} from "../types";

export class SessionsResource {
  constructor(private http: HttpClient) {}

  /** Creates a new checkout session and returns the checkout URL. Throws an error if the checkout URL is not found in the response.
   * @param payload - An object containing the details of the checkout session to be created, including customer email, currency, items, and metadata.
   * example payload:
   * {
   *   customer_email: "test@example.com",
   *   currency: "USD",
   *   items: [
   *     {
   *       id: 1,
   *       name: "Product A",
   *       quantity: 2,
   *       price: 500,
   *     },
   *   ],
   *   metadata: {
   *     orderId: "12345",
   *     customerId: "67890",
   *     sessionId: "abcde",
   *   },
   * }
   * @param options - Optional request options such as retries and timeout settings.
   * @returns An object containing the checkout URL for the created session.
   * @throws Will throw an error if the checkout URL is not found in the response from the API.
  
  */
  async create(
    payload: CheckoutSessionPayload,
    options?: RequestOptions,
  ): Promise<CheckoutSessionResponse> {
    const res = await this.http.request<any>(
      "POST",
      "/session/checkout",
      payload,
      options,
    );

    if (!res?.data?.checkout_url) {
      throw new Error("Checkout URL not found in response");
    }

    return {
      checkout_url: res.data.checkout_url,
    };
  }

  /** Retrieves a checkout session using its unique tracking ID.
   *
   * This method is typically used on merchant success or cancel pages
   * to verify and display the latest payment session status.
   *
   * @param trackId - The unique tracking ID generated for the checkout session.
   *
   * Example:
   * ```ts
   * const session = await gmm.sessions.findByTrackId(
   *   "7107319375257"
   * );
   * ```
   *
   * @returns The checkout session details returned from the GMM API.
   *
   * Example response:
   * ```json
   * {
   *   "statusCode": 200,
   *   "success": true,
   *   "message": "Checkout session retrieved successfully.",
   *   "data": {
   *     "track_id": "7107319375257",
   *     "payment_status": "paid",
   *     "currency": "USD",
   *     "amount": 1000,
   *     "customer_email": "customer@example.com"
   *   }
   * }
   * ```
   *
   * @throws Will throw an error if the session is not found
   * or if the API request fails.
   */
  async findByTrackId(trackId: string) {
    return await this.http.request("GET", `/session/track/${trackId}`);
  }
}
